// TaxDocuments.js
import { useState, useEffect } from "react";
import {
  get1099TaxFigures,
  getSalesTaxReport,
  exportTaxDocumentsCsv,
} from "../../service/Mainservice";
import Loader from "../../Loader/Loader";
import ListControls from "../ListControls/ListControls";
import Pagination from "../Pagination/pagination";
import ExportCsvButton from "../ExportCsvButton";
import "./TaxDocuments.css";

// MUI Icons
import RefreshIcon from "@mui/icons-material/Refresh";
import DescriptionIcon from "@mui/icons-material/Description";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

// Optional: ApexCharts
import Chart from "react-apexcharts";

const TaxDocuments = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [drivers1099, setDrivers1099] = useState([]);
  const [salesTaxData, setSalesTaxData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driverPagination, setDriverPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [salesPagination, setSalesPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [driverSortBy, setDriverSortBy] = useState("fullName");
  const [driverSortOrder, setDriverSortOrder] = useState("asc");
  const [salesSortBy, setSalesSortBy] = useState("state");
  const [salesSortOrder, setSalesSortOrder] = useState("asc");

  const fetchDrivers1099 = async (page = 1, nextSortBy = driverSortBy, nextSortOrder = driverSortOrder) => {
    const res = await get1099TaxFigures({ year, page, pageSize: 10, sortBy: nextSortBy, sortOrder: nextSortOrder });
    setDrivers1099(res.data || []);
    setDriverPagination(res.pagination || { page, limit: 10, total: 0 });
  };

  const fetchSalesTax = async (page = 1, nextSortBy = salesSortBy, nextSortOrder = salesSortOrder) => {
    const res = await getSalesTaxReport({ year, page, pageSize: 10, sortBy: nextSortBy, sortOrder: nextSortOrder });
    setSalesTaxData(res.data || []);
    setSalesPagination(res.pagination || { page, limit: 10, total: 0 });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchDrivers1099(), fetchSalesTax()]);
    } catch (err) {
      console.error("Error fetching tax data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [year]);

  // Summary stats
  const totalDrivers = drivers1099.length;
  const eligibleDrivers = drivers1099.filter((d) => d.isEligibleFor1099).length;
  const totalGrossEarnings = drivers1099.reduce(
    (sum, d) => sum + (d.grossEarnings || 0),
    0,
  );
  const totalTaxCollected = salesTaxData.reduce(
    (sum, s) => sum + (s.taxCollected || 0),
    0,
  );

  // Chart config for Sales Tax by State
  const chartOptions = {
    chart: {
      type: "bar",
      height: 300,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: "50%",
        horizontal: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: salesTaxData.map((s) => s.state),
      title: { text: "State" },
    },
    yaxis: {
      title: { text: "Tax Collected ($)" },
      labels: {
        formatter: (val) => `$${val.toFixed(0)}`,
      },
    },
    colors: ["#133a34", "#eb6718"],
    tooltip: {
      y: {
        formatter: (val) => `$${val.toFixed(2)}`,
      },
    },
  };

  const chartSeries = [
    {
      name: "Tax Collected",
      data: salesTaxData.map((s) => s.taxCollected),
    },
  ];

  return (
    <div className="tax-documents">
      {/* Year Selector */}
      <div className="tax-header">
        <div className="year-selector">
          <label htmlFor="tax-year">Tax Year</label>
          <input
            id="tax-year"
            type="number"
            value={year}
            onChange={(e) =>
              setYear(parseInt(e.target.value) || new Date().getFullYear())
            }
            min={2020}
            max={new Date().getFullYear() + 1}
          />
          <button className="btn-primary" onClick={fetchData}>
            <RefreshIcon fontSize="small" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          {/* Summary Cards */}
          <div className="summary-cards">
            <div className="summary-card">
              <div className="summary-icon" style={{ color: "#133a34" }}>
                <PeopleIcon />
              </div>
              <div className="summary-content">
                <span className="summary-label">Total Drivers</span>
                <span className="summary-value">{totalDrivers}</span>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon" style={{ color: "#eb6718" }}>
                <ReceiptIcon />
              </div>
              <div className="summary-content">
                <span className="summary-label">Eligible for 1099</span>
                <span className="summary-value">{eligibleDrivers}</span>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon" style={{ color: "#3b82f6" }}>
                <AttachMoneyIcon />
              </div>
              <div className="summary-content">
                <span className="summary-label">Total Gross Earnings</span>
                <span className="summary-value">
                  ${totalGrossEarnings.toFixed(2)}
                </span>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon" style={{ color: "#10b981" }}>
                <DescriptionIcon />
              </div>
              <div className="summary-content">
                <span className="summary-label">Total Tax Collected</span>
                <span className="summary-value">
                  ${totalTaxCollected.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Chart Section (if data exists) */}
          {/* {salesTaxData.length > 0 && (
            <div className="chart-wrapper">
              <h3>Sales Tax by State</h3>
              <Chart
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={300}
              />
            </div>
          )} */}

          {/* 1099 Forms Table */}
          <section className="tax-section">
            <div className="section-header">
              <h3>
                <DescriptionIcon /> 1099 Forms for Drivers
              </h3>
              <span className="section-count">{totalDrivers} drivers</span>
            </div>
            <ListControls sortOptions={[{value:"fullName",label:"Driver"},{value:"grossEarnings",label:"Gross earnings"},{value:"isEligibleFor1099",label:"1099 eligibility"}]} sortBy={driverSortBy} sortOrder={driverSortOrder} onSortByChange={(value) => { setDriverSortBy(value); fetchDrivers1099(1, value, driverSortOrder); }} onSortOrderChange={(value) => { setDriverSortOrder(value); fetchDrivers1099(1, driverSortBy, value); }} />
            <div className="table-wrapper">
              <table className="data_table">
                <thead>
                  <tr>
                    <th>Driver</th>
                    <th>Email</th>
                    <th>Gross Earnings</th>
                    <th>Eligible</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers1099.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: "center" }}>
                        No drivers found for {year}.
                      </td>
                    </tr>
                  ) : (
                    drivers1099.map((d) => (
                      <tr key={d.driverId}>
                        <td>{d.fullName}</td>
                        <td>{d.email}</td>
                        <td>${d.grossEarnings.toFixed(2)}</td>
                        <td>
                          {d.isEligibleFor1099 ? (
                            <span className="badge-eligible">Yes</span>
                          ) : (
                            <span className="badge-not-eligible">No</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination pagedata={driverPagination} onPageChange={fetchDrivers1099} />
          </section>

          {/* Sales Tax Report Table */}
          <section className="tax-section">
            <div className="section-header">
              <h3>
                <ReceiptIcon /> Sales Tax Report
              </h3>
              <span className="section-count">
                {salesTaxData.length} states
              </span>
            </div>
            <ListControls sortOptions={[{value:"state",label:"State"},{value:"totalSales",label:"Total sales"},{value:"taxCollected",label:"Tax collected"},{value:"orderCount",label:"Order count"}]} sortBy={salesSortBy} sortOrder={salesSortOrder} onSortByChange={(value) => { setSalesSortBy(value); fetchSalesTax(1, value, salesSortOrder); }} onSortOrderChange={(value) => { setSalesSortOrder(value); fetchSalesTax(1, salesSortBy, value); }} />
            <div className="table-wrapper">
              <table className="data_table">
                <thead>
                  <tr>
                    <th>State</th>
                    <th>Total Sales</th>
                    <th>Tax Collected</th>
                    <th>Order Count</th>
                  </tr>
                </thead>
                <tbody>
                  {salesTaxData.length === 0 ? (
                    <tr>
                      <td colSpan="4" style={{ textAlign: "center" }}>
                        No sales tax data for {year}.
                      </td>
                    </tr>
                  ) : (
                    salesTaxData.map((s) => (
                      <tr key={s.state}>
                        <td>
                          <strong>{s.state}</strong>
                        </td>
                        <td>${s.totalSales.toFixed(2)}</td>
                        <td>${s.taxCollected.toFixed(2)}</td>
                        <td>{s.orderCount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="export-actions">
              <Pagination pagedata={salesPagination} onPageChange={fetchSalesTax} />
              <ExportCsvButton
                exportRequest={exportTaxDocumentsCsv}
                filters={{ type: "1099", year, startDate: `${year}-01-01`, endDate: `${year}-12-31` }}
                filename={`1099_${year}.csv`}
              />
              <ExportCsvButton
                exportRequest={exportTaxDocumentsCsv}
                filters={{ type: "sales_tax", year, startDate: `${year}-01-01`, endDate: `${year}-12-31` }}
                filename={`sales_tax_${year}.csv`}
              />
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default TaxDocuments;
